import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box, Button, Flex, Heading, HStack, Image, Spinner, Text, useColorModeValue, VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentDateRange, DATE_MODES } from '../../utils/dateUtils';
import { serializeDateRangeToUrl } from '../../utils/urlFilterHelpers';
import { safeReturnTo } from '../../utils/returnTo';

/**
 * Shown when Google has authenticated someone who has no Mi Kapital account.
 *
 * The backend cannot give them a session yet — a session row needs a user — so
 * it parks the verified identity in a short-lived httpOnly cookie and sends
 * them here. `pendingIdentity` is that identity, read back through
 * /auth/pending because the cookie itself is not readable from JavaScript.
 *
 * Nothing is asked here. Google's consent screen is where the person agreed to
 * this, and asking again — "create your account?" after they just said yes —
 * only adds a click between consent and the app. So the account is created on
 * arrival and this renders as the pause it actually is. The screen still exists
 * because registration can fail, and a failure needs somewhere to be said.
 *
 * Shares the login page's shell: this is the very next screen in that flow, so
 * a different card would read as a different product.
 */
const SignupPage = () => {
  const { isAuthenticated, loading, needsRegistration, pendingIdentity, register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [failed, setFailed] = useState(false);
  const attempted = useRef(false);
  // Where the person was going when Google sent them here, typically an
  // invitation link. Kept in a ref because `register()` clears
  // `pendingIdentity`, and because the redirect below runs the moment the new
  // session lands, possibly before `navigate()` does: both must agree.
  const destination = useRef(null);

  const iconSrc = useColorModeValue('/brand/app-icon-light.svg', '/brand/app-icon-dark.svg');

  const movementsUrl = useCallback(() => {
    const dateRange = getCurrentDateRange(DATE_MODES.MONTH);
    const { start, end, mode } = serializeDateRangeToUrl(dateRange);
    return `/movements?start=${start}&end=${end}&mode=${mode}`;
  }, []);

  const createAccount = useCallback(async () => {
    attempted.current = true;
    setFailed(false);
    // The backend reduced it to a same-site path when it minted the
    // registration token; checked again here with login()'s own rule, so an
    // auth page or another site is never a destination.
    destination.current = safeReturnTo(pendingIdentity?.return_to);
    try {
      await register();
      // Back where they were headed, or straight to the app. Either way a
      // brand-new user is then held by onboarding's own guard, which is the
      // thing that decides where they actually belong; an invitation link
      // accepts first, so that guard can hold them as a guest.
      navigate(destination.current || movementsUrl(), { replace: true });
    } catch {
      setFailed(true);
    }
  }, [pendingIdentity, register, navigate, movementsUrl]);

  // Fire once, on arrival. The guard is the ref rather than `failed` or a
  // submitting flag, because React 18 runs effects twice in development and two
  // POSTs to /auth/register is not a race worth having. A retry goes through the
  // button, which is a deliberate act and may repeat.
  useEffect(() => {
    if (loading || isAuthenticated || !needsRegistration) return;
    if (attempted.current) return;
    createAccount();
  }, [loading, isAuthenticated, needsRegistration, createAccount]);

  // Only while we're still finding out who this is. `register()` raises the same
  // flag for its own duration, and swapping the card for a bare spinner there
  // would drop the one thing worth showing — whose account is being created.
  if (loading && !attempted.current) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="redesign.bg.page">
        <Spinner size="xl" color="redesign.accent.blue" thickness="4px" />
      </Flex>
    );
  }

  // Already has an account — nothing to create. Also where the new session
  // lands once `register()` sets it, hence the same destination.
  if (isAuthenticated) return <Navigate to={destination.current || movementsUrl()} replace />;

  // No pending registration and not signed in: they arrived here directly.
  if (!needsRegistration) return <Navigate to="/login" replace />;

  return (
    <Flex minH="100vh" align="center" justify="center" bg="redesign.bg.page" px={4}>
      <Flex
        bg="redesign.bg.surface"
        borderWidth="1px"
        borderColor="redesign.border"
        borderRadius="2xl"
        boxShadow="card"
        w="full"
        maxW="440px"
        minH={{ base: 'auto', md: '520px' }}
        px={{ base: 8, md: 12 }}
        py={{ base: 12, md: 16 }}
        direction="column"
        align="center"
        justify="center"
        pb={{ base: 12, md: 24 }}
      >
        <VStack spacing={5} w="full">
          <Image src={iconSrc} alt="Mi Kapital" boxSize="56px" borderRadius="14px" />
          <Heading
            as="h1"
            size="md"
            fontWeight="semibold"
            letterSpacing="-0.02em"
            color="redesign.heading"
            textAlign="center"
          >
            {t('signup.title')}
          </Heading>

          <Box textAlign="center">
            <Text color="redesign.text.muted" fontSize="sm">
              {t('signup.signedInAs')}
            </Text>
            <Text color="redesign.text" fontWeight="semibold" fontSize="sm">
              {pendingIdentity?.email}
            </Text>
          </Box>

          {failed ? (
            <VStack spacing={4} w="full" pt={2}>
              <Box textAlign="center">
                <Text color="redesign.text" fontWeight="semibold" fontSize="sm">
                  {t('signup.errorTitle')}
                </Text>
                <Text color="redesign.text.muted" fontSize="sm">
                  {t('signup.errorDescription')}
                </Text>
              </Box>
              <Button
                w="full"
                size="lg"
                variant="primary"
                fontWeight="medium"
                borderRadius="lg"
                onClick={createAccount}
              >
                {t('signup.retry')}
              </Button>
            </VStack>
          ) : (
            <HStack spacing={3} pt={2} color="redesign.text.muted">
              <Spinner size="sm" color="redesign.accent.blue" thickness="2px" />
              <Text fontSize="sm">{t('signup.creatingAccount')}</Text>
            </HStack>
          )}
        </VStack>
      </Flex>
    </Flex>
  );
};

export default SignupPage;
